import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import SomethingHappened from '@/components/SomethingHappened';
import {
    type FeedingDashboardConfig,
    type FeedingDashboardOverview,
    type FeedingPenOption,
    penDisplayNames,
    type UpdateFeedingDashboardConfig,
} from '@/domain/feedingDashboard';

import { FeedingConfigPanel } from './components/FeedingConfigPanel/FeedingConfigPanel';
import { PenFilterSelect, type PenSelection } from './components/PenFilterSelect/PenFilterSelect';
import { FeedingDashboardFigures } from './FeedingDashboardFigures';
import {
    FeedingDashboardNotices,
    FeedingRefreshHint,
    hasPeriodPens,
} from './FeedingDashboardNotices';

import styles from './FeedingDashboard.module.scss';

/**
 * Enough of the last response to keep the settings panel usable while the pen filter is
 * empty. The panel edits site-level configuration, so it must not vanish just because
 * the charts have nothing to draw.
 */
export interface FeedingConfigSnapshot {
    config: FeedingDashboardConfig;
    pens: { cageId: number; cageName: string; config: FeedingDashboardConfig }[];
}

interface FeedingDashboardViewProps {
    overview?: FeedingDashboardOverview;
    /** Settings to render when `overview` is absent because nothing is selected. */
    configFallback?: FeedingConfigSnapshot;
    /** True when the user deselected every pen, as opposed to data simply being absent. */
    noPensSelected?: boolean;
    /**
     * True once the live-refresh poll budget's fast and slow tiers are spent while
     * `overview.feeding.refreshing` stays true. Polling keeps going at a slower,
     * trailing interval past this point, so this only changes which sentence is shown.
     */
    pollingSlow?: boolean;
    /**
     * True while `overview` is the previous period's or pen selection's response, kept on
     * screen until the new one arrives (`isPreviousData`). A refresh poll of the same
     * selection leaves it false, so polling never dims the page.
     */
    updating?: boolean;
    /**
     * The `feeding-pellet-fallthrough` flag. Off hides every pellet element on its own,
     * whatever the response says, so a replica that ignores `includeFallthrough` still
     * shows nothing unapproved.
     */
    pelletEnabled: boolean;
    loading: boolean;
    error: unknown;
    /** Try again in the error state. */
    onRetry?: () => void;
    isRetrying?: boolean;
    /** Every pen of the period, whatever is selected. */
    penOptions: FeedingPenOption[];
    penSelection: PenSelection;
    dateRange: DateRange;
    savingConfig: boolean;
    onPenChange: (selection: PenSelection) => void;
    onDateChange: (range: DateRange) => void;
    onSaveConfig: (update: UpdateFeedingDashboardConfig) => Promise<void>;
    onSavePenConfig: (cageId: number, update: UpdateFeedingDashboardConfig) => Promise<void>;
    /**
     * The site's data sources page, for an OptoScale admin while the provider-config-ui flag
     * is on. Null renders the page without any link to it.
     */
    dataSourcesHref?: string | null;
}

export function FeedingDashboardView(props: FeedingDashboardViewProps) {
    const {
        overview,
        configFallback,
        noPensSelected,
        pollingSlow = false,
        updating = false,
        pelletEnabled,
        loading,
        error,
        onRetry,
        isRetrying,
        penOptions,
        penSelection,
        dateRange,
        savingConfig,
        onPenChange,
        onDateChange,
        onSaveConfig,
        onSavePenConfig,
        dataSourcesHref = null,
    } = props;
    const { t } = useTranslation();
    // The period can list a historical pen under the current pen's name. The KPI notes, the
    // per-pen charts and the table label pens by the rule the selector and the notices use,
    // from this response's own pens, so the two read as different pens and a chart keyed by
    // label cannot merge them.
    const availablePens = overview?.availablePens;
    const penNames = useMemo(() => penDisplayNames(availablePens ?? []), [availablePens]);
    // The live response wins; the snapshot only stands in when there is no response
    // because the pen filter is empty.
    const configPanel: FeedingConfigSnapshot | undefined = overview
        ? {
              config: overview.config,
              pens: overview.pens.map((pen) => ({
                  cageId: pen.cageId,
                  cageName: pen.cageName,
                  config: pen.config,
              })),
          }
        : configFallback;
    // One condition for every pellet element on the page, text included, so none can
    // show alone: the flag first, then the hardware (IPS req 26 and 27). The settings
    // follow the flag alone, since the hardware here covers only the filtered pens.
    const pelletShown = pelletEnabled && (overview?.summary.fallthroughSupported ?? false);

    return (
        <PageLayout
            title={t('feedingModule.title')}
            className={styles.page}
            actions={
                <div className={styles.toolbar}>
                    <PenFilterSelect
                        options={penOptions}
                        selection={penSelection}
                        onChange={onPenChange}
                    />
                    <DateRangePicker
                        dateFrom={dateRange.dateFrom}
                        dateTo={dateRange.dateTo}
                        defaultPresetId="last4weeks"
                        onDateChange={onDateChange}
                        menuPosition="right"
                    />
                    {configPanel ? (
                        <FeedingConfigPanel
                            config={configPanel.config}
                            pens={configPanel.pens}
                            showPelletSettings={pelletEnabled}
                            saving={savingConfig}
                            onSave={onSaveConfig}
                            onSavePen={onSavePenConfig}
                            dataSourcesHref={dataSourcesHref}
                        />
                    ) : null}
                </div>
            }>
            <FeedingRefreshHint feeding={overview?.feeding} pollingSlow={pollingSlow} />

            {noPensSelected ? (
                <p className={styles.emptySelection}>{t('feedingModule.filter.noPensSelected')}</p>
            ) : null}

            {error ? (
                <SomethingHappened onRetry={onRetry} isRetrying={isRetrying} />
            ) : loading ? (
                <Spinner size="lg" />
            ) : (
                <>
                    {overview ? (
                        <FeedingDashboardNotices
                            overview={overview}
                            updating={updating}
                            dataSourcesHref={dataSourcesHref}
                        />
                    ) : null}
                    {overview && hasPeriodPens(overview) ? (
                        <FeedingDashboardFigures
                            overview={overview}
                            penNames={penNames}
                            pelletShown={pelletShown}
                            updating={updating}
                            dataSourcesHref={dataSourcesHref}
                        />
                    ) : null}
                </>
            )}
        </PageLayout>
    );
}
